import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "../index.js";
import { useApi } from "../../hooks/useApi.js";
import { ProductOrderList } from "./ProductOrderList.jsx";
import { ProductOrderDetail } from "./ProductOrderDetail.jsx";
import { billingTabParams, readProductBillingFilters, writeProductBillingFilter } from "../../lib/productOrders.js";

/**
 * The billing desk's tab row: the four contract tabs the desk always had, and
 * Product orders. A tab switch carries ONLY the tab and the chosen client
 * (billingTabParams) — page, search, sort, month and the product filters are
 * all dropped, so each tab starts from its own explicit defaults and nothing
 * from one list's state reaches the other list's query.
 */
export const BILLING_TABS = [
  { key: "to_invoice", label: "To invoice" },
  { key: "awaiting", label: "Awaiting payment" },
  { key: "paid", label: "Paid" },
  { key: "all", label: "All" },
  { key: "product_orders", label: "Product orders" },
];

export function BillingTabs({ active, onChange }) {
  return (
    <>
      {BILLING_TABS.map((t) => (
        <button key={t.key} type="button" onClick={() => onChange(t.key)} aria-pressed={active === t.key}
          className={`px-3 py-1 rounded-full text-[12px] font-medium border ${active === t.key ? "bg-accent text-white border-accent" : "border-border-default text-text-secondary hover:text-text-primary"}`}>
          {t.label}
        </button>
      ))}
    </>
  );
}

/**
 * Delma's Product Orders tab on the billing desk (Task 6).
 *
 * Approved print and promotional orders ready to be invoiced in QuickBooks
 * Desktop, then the record of which QuickBooks invoice each one went on.
 * Prism does not create invoices: the accountant copies the billing
 * description into QuickBooks and records the invoice number and date here.
 *
 * This component is mounted INSTEAD of the contract billing component, never
 * beside it, so none of the contract desk's hooks run while it is shown and
 * no product filter can reach /api/billing/items. Its filters live under
 * their own URL keys (productStatus, productSearch, productPage) read and
 * written only here; the client filter is the one key shared with the
 * contract tabs because it means the same thing on both.
 *
 * The list is ProductOrderList in billing mode (Ready for billing / Invoiced
 * / All history — the history shows cancelled and awaiting orders too, and
 * the summary line counts only ready orders matching the client/search,
 * whatever the chosen filter or page). The detail is ProductOrderDetail in
 * billing mode: an accountant records invoices and nothing else; an admin
 * also has revise and correction. There is no mark-paid for product orders —
 * payment is tracked in QuickBooks.
 */
export function ProductOrdersBilling() {
  const [params, setParams] = useSearchParams();
  const filters = readProductBillingFilters(params);
  const setFilter = (key, value) => setParams((p) => writeProductBillingFilter(p, key, value), { replace: true });
  const clientsApi = useApi("/api/me/clients");
  const clients = (clientsApi.data?.clients ?? []).filter((c) => typeof c?.id === "string" && typeof c?.name === "string").slice().sort((a, b) => a.name.localeCompare(b.name));
  const [selectedId, setSelectedId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="max-w-[1200px] space-y-4">
      <Card className="p-0">
        <div className="px-4 py-2.5 flex flex-wrap items-center gap-2">
          <BillingTabs active="product_orders" onChange={(tab) => { setSelectedId(null); setParams(billingTabParams(tab, filters.clientId), { replace: true }); }} />
        </div>
      </Card>

      <Card>
        <div className="px-4 py-3 text-[12px] text-text-secondary">
          <span className="font-medium text-text-primary">Product orders.</span> Approved print and promotional requests, ready to invoice in QuickBooks Desktop. Copy the billing description into the invoice, then record the QuickBooks invoice number and date here. Payment is tracked in QuickBooks, not in Prism.
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(360px,440px)] gap-4 items-start">
        <ProductOrderList mode="billing" clients={clients} filters={filters} setFilter={setFilter} selectedId={selectedId} refreshKey={refreshKey} onSelect={setSelectedId} />
        {selectedId ? (
          <ProductOrderDetail id={selectedId} mode="billing" onClose={() => setSelectedId(null)} onChanged={() => setRefreshKey((k) => k + 1)} />
        ) : (
          <Card><div className="px-5 py-10 text-center text-[13px] text-text-muted">Select an order to see its billing description and record its invoice.</div></Card>
        )}
      </div>
    </div>
  );
}
